import { useEffect, useMemo, useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { Button, Field, Modal, PlusIcon, SearchIcon, StatusBadge, ToggleField, UserIcon, XIcon } from "@fawkes/ui";

import type { TaakTeam } from "./UserTeamsSection";

/** Iemand in een team, zoals het teamsbeheer hem toont. */
interface TeamLid {
  team: string;
  gebruikerId: string;
  naam: string;
  email: string | null;
  actief: boolean;
}

interface Gebruiker {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  loginName?: string | null;
  email?: string | null;
  active: boolean;
}

/**
 * De teams van de takenmodule beheren (Maarten, 2026-10-09: "in de stijl van het programma, zien wie er gekoppeld zijn,
 * uitgebreider"). Links de teams met hun aantal leden, rechts het gekozen team: naam, aan/uit en de leden, die je hier
 * ook toevoegt en weghaalt. Bij een gebruiker kun je zijn teams nog steeds kiezen; dit is dezelfde koppeling van de
 * andere kant.
 *
 * <p>Uitzetten en niet verwijderen: taken en taakregels wijzen met de code naar een team, en die blijven leesbaar. Een
 * uitgezet team is alleen niet meer te kiezen; aanzetten kan altijd weer.
 */
export function TeamsBeheer({ onSluit }: { onSluit: () => void }) {
  const t = useTranslate();
  const lijst = useCustom<TaakTeam[]>({ url: "taak-teams?alle=true", method: "get" });
  const ledenQuery = useCustom<TeamLid[]>({ url: "taak-teams/leden", method: "get" });
  const { mutate: stuur, isLoading: bezig } = useCustomMutation();
  const teams = Array.isArray(lijst.data?.data) ? lijst.data!.data : [];
  const leden = Array.isArray(ledenQuery.data?.data) ? ledenQuery.data!.data : [];

  const [gekozen, setGekozen] = useState<string | null>(null);
  const [nieuw, setNieuw] = useState("");
  const [naam, setNaam] = useState("");
  const [zoek, setZoek] = useState("");
  const [fout, setFout] = useState<string | null>(null);

  // Begin bij het eerste team; na het aanmaken van een team staat dat nieuwe team open.
  useEffect(() => {
    if (!gekozen && teams.length > 0) setGekozen(teams[0].code);
  }, [gekozen, teams]);
  const team = teams.find((x) => x.code === gekozen) ?? null;
  useEffect(() => { setNaam(team?.naam ?? ""); setZoek(""); }, [team?.code, team?.naam]);

  const ledenPerTeam = useMemo(() => {
    const m = new Map<string, TeamLid[]>();
    for (const l of leden) m.set(l.team, [...(m.get(l.team) ?? []), l]);
    return m;
  }, [leden]);
  const ledenVanTeam = team ? ledenPerTeam.get(team.code) ?? [] : [];

  // Zoeken onder de gebruikers om iemand toe te voegen; alleen als er iets getypt is.
  const zoekterm = zoek.trim();
  const gebruikersQuery = useCustom<{ rows: Gebruiker[] }>({
    url: `users?q=${encodeURIComponent(zoekterm)}&size=8&page=0`, method: "get",
    queryOptions: { enabled: zoekterm.length >= 2 },
  });
  const kandidaten = zoekterm.length >= 2 && Array.isArray(gebruikersQuery.data?.data?.rows)
    ? gebruikersQuery.data!.data.rows.filter((g) => !ledenVanTeam.some((l) => l.gebruikerId.toLowerCase() === g.id.toLowerCase()))
    : [];

  const fouten = (e: unknown) => setFout(melding(e) ?? t("teamsBeheer.mislukt"));
  const ververs = () => { setFout(null); void lijst.refetch(); void ledenQuery.refetch(); };

  function maak() {
    if (!nieuw.trim()) return;
    stuur({ url: "taak-teams", method: "post", values: { naam: nieuw.trim() } }, {
      onSuccess: (r) => {
        setNieuw("");
        const code = (r?.data as { code?: string } | undefined)?.code;
        if (code) setGekozen(code);
        ververs();
      },
      onError: fouten,
    });
  }

  function wijzig(naamNieuw: string, actief: boolean) {
    if (!team) return;
    stuur({ url: `taak-teams/${encodeURIComponent(team.code)}`, method: "put", values: { naam: naamNieuw, actief } },
      { onSuccess: ververs, onError: fouten });
  }

  function voegToe(g: Gebruiker) {
    if (!team) return;
    stuur({ url: `taak-teams/${encodeURIComponent(team.code)}/leden`, method: "post", values: { gebruikerId: g.id } },
      { onSuccess: () => { setZoek(""); ververs(); }, onError: fouten });
  }

  function haalWeg(l: TeamLid) {
    if (!team) return;
    stuur({ url: `taak-teams/${encodeURIComponent(team.code)}/leden/${l.gebruikerId}`, method: "delete", values: {} },
      { onSuccess: ververs, onError: fouten });
  }

  const naamGewijzigd = team != null && naam.trim() !== "" && naam.trim() !== team.naam;

  return (
    <Modal open size="wide" title={t("teamsBeheer.titel")} subtitle={t("teamsBeheer.uitleg")} onClose={onSluit}
      footer={<Button variant="secondary" onClick={onSluit}>{t("common.close")}</Button>}>
      <div className="tmb">
        <aside className="tmb-lijst">
          <form className="tmb-nieuw" onSubmit={(e) => { e.preventDefault(); maak(); }}>
            <input className="fui-field__input" value={nieuw} maxLength={100} placeholder={t("teamsBeheer.nieuwPlaats")}
              aria-label={t("teamsBeheer.nieuw")} onChange={(e) => setNieuw(e.target.value)} />
            <Button type="submit" disabled={bezig || !nieuw.trim()} aria-label={t("teamsBeheer.nieuw")} title={t("teamsBeheer.nieuw")}>
              <PlusIcon width={16} height={16} />
            </Button>
          </form>
          <ul className="tmb-teams" role="listbox" aria-label={t("teamsBeheer.titel")}>
            {teams.map((x) => {
              const aantal = ledenPerTeam.get(x.code)?.length ?? 0;
              return (
                <li key={x.code}>
                  <button type="button" role="option" aria-selected={x.code === gekozen}
                    className={`tmb-team${x.code === gekozen ? " is-gekozen" : ""}${x.actief ? "" : " is-uit"}`}
                    onClick={() => { setGekozen(x.code); setFout(null); }}>
                    <span className="tmb-team__icoon" aria-hidden="true"><UserIcon width={18} height={18} /></span>
                    <span className="tmb-team__tekst">
                      <strong>{x.naam}</strong>
                      <span>{aantal === 1 ? t("teamsBeheer.eenLid") : t("teamsBeheer.leden", { args: [String(aantal)] })}</span>
                    </span>
                    {!x.actief && <StatusBadge tone="neutral">{t("teamsBeheer.uitgezet")}</StatusBadge>}
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        <section className="tmb-detail">
          {!team ? (
            <p className="tmb-leeg">{t("teamsBeheer.kiesTeam")}</p>
          ) : (
            <>
              <div className="tmb-kop">
                <div className="tmb-naam">
                  <Field label={t("teamsBeheer.naam")} value={naam} maxLength={100} onChange={(e) => setNaam(e.target.value)} />
                  {naamGewijzigd && (
                    <Button variant="primary" disabled={bezig} onClick={() => wijzig(naam.trim(), team.actief)}>
                      {t("common.save")}
                    </Button>
                  )}
                </div>
                <ToggleField label={t(team.actief ? "teamsBeheer.actief" : "teamsBeheer.uitgezet")} checked={team.actief}
                  disabled={bezig} onChange={(e) => wijzig(team.naam, e.target.checked)} />
              </div>
              {!team.actief && <p className="tmb-hint">{t("teamsBeheer.uitgezetUitleg")}</p>}

              <div className="tmb-leden">
                <div className="tmb-leden__kop">
                  <h3>{t("teamsBeheer.ledenTitel")}</h3>
                  <span>{ledenVanTeam.length}</span>
                </div>
                <div className="tmb-zoek">
                  <span className="tmb-zoek__icoon" aria-hidden="true"><SearchIcon width={16} height={16} /></span>
                  <input className="fui-field__input" value={zoek} placeholder={t("teamsBeheer.lidZoeken")}
                    aria-label={t("teamsBeheer.lidZoeken")} onChange={(e) => setZoek(e.target.value)} />
                  {zoekterm.length >= 2 && (
                    <ul className="tmb-kandidaten">
                      {kandidaten.length === 0 ? (
                        <li className="tmb-kandidaten__leeg">
                          {gebruikersQuery.isFetching ? t("common.loading") : t("teamsBeheer.geenGebruikers")}
                        </li>
                      ) : kandidaten.map((g) => (
                        <li key={g.id}>
                          <button type="button" onClick={() => voegToe(g)} disabled={bezig}>
                            <Initialen naam={volledigeNaam(g)} />
                            <span className="tmb-persoon">
                              <strong>{volledigeNaam(g)}</strong>
                              <span>{g.email || g.loginName}</span>
                            </span>
                            <PlusIcon width={16} height={16} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {ledenVanTeam.length === 0 ? (
                  <p className="tmb-leeg">{t("teamsBeheer.geenLeden")}</p>
                ) : (
                  <ul className="tmb-ledenlijst">
                    {ledenVanTeam.map((l) => (
                      <li key={l.gebruikerId} className={l.actief ? undefined : "is-uit"}>
                        <Initialen naam={l.naam} />
                        <span className="tmb-persoon">
                          <strong>{l.naam}</strong>
                          <span>{l.email ?? ""}</span>
                        </span>
                        {!l.actief && <StatusBadge tone="neutral">{t("teamsBeheer.accountUit")}</StatusBadge>}
                        <button type="button" className="tmb-weg" disabled={bezig} onClick={() => haalWeg(l)}
                          aria-label={t("teamsBeheer.lidWeg", { args: [l.naam] })} title={t("teamsBeheer.lidWeg", { args: [l.naam] })}>
                          <XIcon width={16} height={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
          {fout && <p className="fawkes-error" role="alert">{fout}</p>}
        </section>
      </div>
    </Modal>
  );
}

function volledigeNaam(g: Gebruiker): string {
  return [g.firstName, g.lastName].filter(Boolean).join(" ") || g.loginName || "—";
}

function Initialen({ naam }: { naam: string }) {
  const letters = naam.split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return <span className="tmb-avatar" aria-hidden="true">{letters || "?"}</span>;
}

/** De melding van de api ("Er is al een team met de naam …"), als die er is. */
function melding(e: unknown): string | null {
  const m = (e as { message?: unknown } | null)?.message;
  return typeof m === "string" && m.trim() && !/^Request failed/i.test(m) ? m : null;
}
